import { useCallback } from 'react';
import { TStatusBadgeProps } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';

import { IWallet, walletStatusList } from '../store';

import { IUser } from '@/features/user/store/user.model';

import { useFavorites } from '@/hooks/useFavorites';

import { useMatomo } from '@/features/matomo/useMatomo';

interface IUseUserWallet {
  user: IUser;
  wallet: IWallet;
}
export function useUserWallet({ user, wallet }: IUseUserWallet) {
  const { t } = useTranslation();
  const { triggerEvent } = useMatomo();

  const { handleFavorite, isStarred, isLoading, currentFavoriteId } = useFavorites();

  const onChangeFavorite = useCallback(() => {
    const { walletId, iban, eventName, userId, userFirstname, userLastname } = wallet;

    const userName =
      userFirstname === '' && userLastname === '' ? (user?.legalName ?? '') : `${userFirstname} ${userLastname}`;

    const favoriteWallet = {
      id: walletId,
      walletId,
      iban,
      name: eventName,
      userId,
      userName,
    };

    const isWalletStarred = isStarred('wallet', walletId);

    triggerEvent('starred-wallet', {
      'starred-wallet-action': isWalletStarred ? 'remove' : 'add',
      'starred-wallet-action-value': isWalletStarred ? -1 : 1,
    });

    handleFavorite({ type: 'wallet', ...favoriteWallet });
  }, [wallet, handleFavorite, user, isStarred, triggerEvent]);

  const getWalletStatus = (status: keyof typeof walletStatusList): Pick<TStatusBadgeProps, 'color' | 'label'> => {
    const label = t(`pages.currentProfiles.wallets.wallet.statusLabel.${status}`);
    if (status === 'PENDING')
      return {
        label,
        color: 'orange',
      };
    if (status === 'VALIDATED') return { label, color: 'green' };
    if (status === 'CANCELED') return { label, color: 'red' };
    return { label, color: 'gray' };
  };

  const getWalletType = (typeId?: number) => t(`pages.currentProfiles.profile.overview.type.${typeId || '9'}`);

  return {
    statusProps: getWalletStatus(wallet.walletStatus),
    onChangeFavorite,
    isStarred: isStarred('wallet', wallet.walletId),
    walletType: getWalletType(wallet?.walletTypeId),
    isLoading: isLoading && currentFavoriteId === wallet.walletId,
  };
}
